import { playDialTone, playDialToneUrl } from '../audio-manager'
import { recordCallFlow } from '../../debug/call-flow-log'
import { getNativePlatform } from '../../utils/platform'
import { isNativeRingbackActive, isNativeRingbackPending } from './native-call'

export type HtmlRingbackRekickResult = 'suppressed' | 'played'

/**
 * Legacy HTML5 outgoing ringback re-kick (PRs #164/#169).
 *
 * After the mic-permission probe, startCallWithMedia used to restart HTML5
 * dial tone on native because getUserMedia killed the WebView player. Native
 * ringback now lives on ConnectingTonePlayer (PRs #359/#367); overlapping
 * HTML5 is the last double-dial-tone path when the OS does not mute WebView
 * audio. Native ownership starts at REQUEST (pending), not at started — #371
 * gated only on `active`, and the re-kick fired inside the 782ms
 * requested-but-not-started native prepare window (call 509d61f1, build 110).
 * Keep the fallback — if the native request ultimately fails, both flags
 * clear and the ringtone hook's html5Fallback path plays the tone.
 */
export function rekickHtml5OutgoingDialTone(input: {
  callId: string
  playUrl?: string | null
  repeatCount?: number
}): HtmlRingbackRekickResult {
  const active = isNativeRingbackActive()
  const pending = isNativeRingbackPending()
  if (active || pending) {
    recordCallFlow(
      'ctrl:ringback:htmlRekickSuppressed',
      { platform: getNativePlatform(), reason: active ? 'active' : 'pending' },
      { callId: input.callId },
    )
    return 'suppressed'
  }

  const playUrl = input.playUrl
  const repeatCount = input.repeatCount ?? 0
  if (playUrl && repeatCount > 0) {
    playDialToneUrl(playUrl, { repeatCount })
  } else if (playUrl) {
    playDialToneUrl(playUrl, { loop: true })
  } else {
    playDialTone()
  }
  recordCallFlow(
    'ctrl:ringback:htmlRekick',
    { platform: getNativePlatform() },
    { callId: input.callId },
  )
  return 'played'
}
